/**
 * Thin axios wrappers for the Channel Planner (Outreach MVP).
 *
 * Three endpoints, all read-gated the same as the Mail List preview
 * ({analyst, admin, org_manager, manager, sms_manager}):
 *
 *   GET  /outreach/channels          — the channel catalog + default criteria
 *       weights that drive the decision matrix. No body.
 *   POST /outreach/cost-per-vote     — cost-per-expected-vote for each channel,
 *       for an `AudienceSpec` (same shape MailListBuilder POSTs). Already sorted
 *       cheapest-first.
 *   POST /outreach/decision-matrix   — the weighted decision matrix: per-channel
 *       criteria scores + a weighted total. Accepts the same `AudienceSpec` PLUS
 *       optional `weights` / `manual_scores` overrides. Already ranked best-first.
 *
 * All requests go through the shared `client` (base URL + Authorization header
 * injection live there). Tenant scope is enforced server-side by the JWT
 * middleware. Pattern mirrors `src/api/mailList.ts`.
 *
 * The audience shape (`AudienceSpec`) and the candidate multi-select
 * (`listCandidates`) are reused verbatim from `@/api/mailList` so a Channel
 * Planner call site needs only this one import.
 */
import client from '@/api/client';
import {
  listCandidates,
  type AudienceSpec,
  type MailListTier,
} from '@/api/mailList';

// Re-export so a channel-planner call site pulls everything from one place.
export { listCandidates };
export type { AudienceSpec, MailListTier };

// ---------------------------------------------------------------------------
// Request shape — the audience spec the Channel Planner sends
// ---------------------------------------------------------------------------

/**
 * The audience definition POSTed to both `/cost-per-vote` and
 * `/decision-matrix`. Structurally identical to `AudienceSpec` but with `tier`
 * required (the planner always analyzes one cumulative bundle at a time).
 */
export interface ChannelPlanSpec extends AudienceSpec {
  tier: MailListTier;
}

/** Optional overrides layered onto a `ChannelPlanSpec` for `/decision-matrix`. */
export interface DecisionMatrixOverrides {
  /** Per-criterion weights, keyed by criterion (audience_fit, cost_efficiency, …). */
  weights?: Record<string, number>;
  /** Per-channel, per-criterion manual score overrides (1–5). */
  manual_scores?: Record<string, Record<string, number>>;
}

// ---------------------------------------------------------------------------
// Response shape — GET /outreach/channels
// ---------------------------------------------------------------------------

/** One channel in the catalog. `scores` is per-criterion (1–5) baseline. */
export interface ChannelCatalogEntry {
  label: string;
  unit: string;
  delivery_factor: number;
  scores: Record<string, number>;
}

/** The channel catalog + default criteria weights. */
export interface ChannelsCatalog {
  channels: Record<string, ChannelCatalogEntry>;
  criteria_weights: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Response shape — POST /outreach/cost-per-vote
// ---------------------------------------------------------------------------

/**
 * One channel's cost-per-expected-vote row. `cost_per_vote_cents` is null when
 * expected reach is zero (division undefined) — render a dash, not "$0.00".
 */
export interface ChannelCostRow {
  channel: string;
  label: string;
  cost_cents: number;
  expected_reach: number;
  cost_per_vote_cents: number | null;
}

/** Full cost-per-vote response. `channels` arrives sorted cheapest-first. */
export interface CostPerVoteResponse {
  tier: MailListTier;
  households: number;
  voters: number;
  election_date: string;
  channels: ChannelCostRow[];
  notes: string[];
}

// ---------------------------------------------------------------------------
// Response shape — POST /outreach/decision-matrix
// ---------------------------------------------------------------------------

/** One channel row of the weighted decision matrix. */
export interface DecisionMatrixRow {
  channel: string;
  label: string;
  /** Per-criterion scores (1–5), keyed by the criterion. */
  scores: Record<string, number>;
  weighted_total: number;
  cost_cents: number;
  expected_reach: number;
  cost_per_vote_cents: number | null;
}

/** Full decision-matrix response. `channels` arrives ranked best-first. */
export interface DecisionMatrixResponse {
  tier: MailListTier;
  households: number;
  voters: number;
  criteria: string[];
  weights: Record<string, number>;
  channels: DecisionMatrixRow[];
  notes: string[];
}

// ---------------------------------------------------------------------------
// Request shape — POST /outreach/contact-plan/optimize
// ---------------------------------------------------------------------------

/**
 * The budget-optimizer request. Structurally the audience spec the planner
 * already builds (`ChannelPlanSpec`) PLUS a spend cap, an optional paid-channel
 * allow-list, and the deepest cumulative tier the optimizer may fund.
 *
 * `channels` omitted = the backend considers all eligible paid channels. Note
 * `max_tier` reuses `MailListTier` — the optimizer buys tiers cheapest/highest-
 * value first up to (and including) this bundle.
 */
export interface ContactPlanSpec extends ChannelPlanSpec {
  budget_cents: number;
  channels?: string[];
  max_tier: MailListTier;
}

// ---------------------------------------------------------------------------
// Response shape — POST /outreach/contact-plan/optimize
// ---------------------------------------------------------------------------

/** One funded line of the optimized plan (a contest × tier × channel buy). */
export interface PlanRow {
  contest: string;
  tier: string;
  channel: string;
  channel_label: string;
  households: number;
  voters: number;
  cost_cents: number;
  expected_reach: number;
}

/** One contest's aggregate coverage across the tiers the plan funded for it. */
export interface CoverageRow {
  contest: string;
  tiers: string[];
  cost_cents: number;
  expected_reach: number;
}

/** Full budget-optimizer response. */
export interface ContactPlanResponse {
  budget_cents: number;
  spent_cents: number;
  leftover_cents: number;
  total_expected_reach: number;
  funded_items: number;
  plan: PlanRow[];
  coverage: CoverageRow[];
  advisory: string | null;
  notes: string[];
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * Fetch the channel catalog + default criteria weights. Read-gated the same as
 * the Mail List preview. No body.
 */
export async function getChannels(): Promise<ChannelsCatalog> {
  const res = await client.get<ChannelsCatalog>('/outreach/channels');
  return res.data;
}

/**
 * Compute cost-per-expected-vote for each channel for an audience. The
 * response's `channels` array is already sorted cheapest-first.
 */
export async function getCostPerVote(spec: ChannelPlanSpec): Promise<CostPerVoteResponse> {
  const res = await client.post<CostPerVoteResponse>('/outreach/cost-per-vote', spec);
  return res.data;
}

/**
 * Compute the weighted decision matrix for an audience. Optional `weights` /
 * `manual_scores` overrides ride along in the same body. The response's
 * `channels` array is already ranked best-first.
 */
export async function getDecisionMatrix(
  spec: ChannelPlanSpec,
  overrides?: DecisionMatrixOverrides,
): Promise<DecisionMatrixResponse> {
  const body = overrides ? { ...spec, ...overrides } : spec;
  const res = await client.post<DecisionMatrixResponse>('/outreach/decision-matrix', body);
  return res.data;
}

/**
 * Optimize a funded contact plan against a budget. Sends the same audience spec
 * the planner already builds plus a `budget_cents` cap, an optional paid-channel
 * allow-list, and the deepest tier the optimizer may fund. The response's `plan`
 * is the funded buys and `coverage` rolls them up per contest.
 */
export async function getContactPlan(spec: ContactPlanSpec): Promise<ContactPlanResponse> {
  const res = await client.post<ContactPlanResponse>('/outreach/contact-plan/optimize', spec);
  return res.data;
}
